import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as tus from "tus-js-client";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Edit, Plus, X } from "lucide-react";
import Navbar from "@/components/Navbar";

const STORAGE_BUCKET = "portfolio-videos";
const RESUMABLE_UPLOAD_THRESHOLD = 6 * 1024 * 1024;
const RESUMABLE_CHUNK_SIZE = 6 * 1024 * 1024;
const MAX_VIDEO_SIZE = 500 * 1024 * 1024;
const RESUMABLE_RETRY_DELAYS = [0, 3000, 5000, 10000, 20000];

interface PortfolioVideo {
  id: string;
  title: string;
  description: string | null;
  industry: string;
  location: string;
  video_url: string;
  thumbnail_url: string | null;
  campaign_type: string | null;
  views: number;
  likes: number;
  comments: number;
  conversions: number;
  revenue: number;
  roi_multiplier: number;
  testimonial: string | null;
  order_index: number;
  conversion_type: string;
  format_type: string | null;
  created_at: string;
}

type VideoFormat = "short" | "long" | "viral";

type VideoFormState = {
  title: string;
  description: string;
  industry: string;
  location: string;
  campaign_type: string;
  format_type: VideoFormat;
  views: number;
  likes: number;
  comments: number;
  conversions: number;
  revenue: number;
  roi_multiplier: number;
  testimonial: string;
  order_index: number;
  conversion_type: string;
};

const formatFileSize = (bytes: number) => {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && error && "message" in error) {
    return String((error as { message?: unknown }).message ?? "Something went wrong");
  }
  return "Something went wrong";
};

const getPublicAssetUrl = (path: string) => {
  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
  return data.publicUrl;
};

const getResumableUploadEndpoint = () => {
  const url = new URL(import.meta.env.VITE_SUPABASE_URL);
  url.hostname = url.hostname.replace(".supabase.co", ".storage.supabase.co");
  return `${url.origin}/storage/v1/upload/resumable`;
};

const AdminVideos = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState<string | null>(null);
  const [videos, setVideos] = useState<PortfolioVideo[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [formData, setFormData] = useState<VideoFormState>({
    title: "",
    description: "",
    industry: "",
    location: "",
    campaign_type: "",
      format_type: "short",
    views: 0,
    likes: 0,
    comments: 0,
    conversions: 0,
    revenue: 0,
    roi_multiplier: 0,
    testimonial: "",
    order_index: 0,
    conversion_type: "calls",
  });
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);

  useEffect(() => {
    const checkAuthAndLoad = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate("/admin");
        return;
      }

      // Check admin role
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .single();

      if (!roleData || roleData.role !== "admin") {
        navigate("/");
        return;
      }

      await loadVideos();
    };

    checkAuthAndLoad();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        navigate("/admin");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!uploading) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [uploading]);

  const loadVideos = async () => {
    try {
      const { data, error } = await supabase
        .from("portfolio_videos")
        .select("*")
        .order("order_index", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) throw error;
      setVideos(data || []);
    } catch (error) {
      console.error("Error loading videos:", error);
      toast.error("Failed to load videos");
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (
    file: File,
    objectPath: string,
    options: { progressStart: number; progressEnd: number; label: string },
  ) => {
    const { progressStart, progressEnd, label } = options;
    setUploadStage(label);
    setUploadProgress(progressStart);

    if (file.size <= RESUMABLE_UPLOAD_THRESHOLD) {
      const { error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(objectPath, file, {
          upsert: true,
          contentType: file.type || undefined,
        });

      if (error) throw error;
      setUploadProgress(progressEnd);
      return getPublicAssetUrl(objectPath);
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error("Your admin session expired. Please sign in again before uploading.");
    }

    await new Promise<void>((resolve, reject) => {
      const upload = new tus.Upload(file, {
        endpoint: getResumableUploadEndpoint(),
        retryDelays: RESUMABLE_RETRY_DELAYS,
        headers: {
          authorization: `Bearer ${session.access_token}`,
          "x-upsert": "true",
        },
        uploadDataDuringCreation: true,
        removeFingerprintOnSuccess: true,
        chunkSize: RESUMABLE_CHUNK_SIZE,
        metadata: {
          bucketName: STORAGE_BUCKET,
          objectName: objectPath,
          contentType: file.type || "application/octet-stream",
          cacheControl: "3600",
        },
        onError: (error) => reject(error),
        onProgress: (bytesUploaded, bytesTotal) => {
          const ratio = bytesTotal > 0 ? bytesUploaded / bytesTotal : 0;
          const progress = progressStart + ratio * (progressEnd - progressStart);
          setUploadProgress(Math.min(100, Math.round(progress)));
        },
        onSuccess: () => {
          setUploadProgress(progressEnd);
          resolve();
        },
      });

      upload.findPreviousUploads()
        .then((previousUploads) => {
          if (previousUploads.length > 0) {
            upload.resumeFromPreviousUpload(previousUploads[0]);
          }
          upload.start();
        })
        .catch(reject);
    });

    return getPublicAssetUrl(objectPath);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (videoFile && videoFile.size > MAX_VIDEO_SIZE) {
      toast.error(`Video is too large. Max size is ${formatFileSize(MAX_VIDEO_SIZE)}.`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStage(editingId ? "Updating video" : "Uploading video");

    let videoUrl = "";
    let thumbnailUrl = "";

    try {
      // Upload video
      if (videoFile) {
        const videoExt = videoFile.name.split('.').pop();
        const videoPath = `${crypto.randomUUID()}.${videoExt}`;

        videoUrl = await uploadFile(videoFile, videoPath, {
          progressStart: 0,
          progressEnd: thumbnailFile ? 90 : 100,
          label: "Uploading video file",
        });
      }

      // Upload thumbnail
      if (thumbnailFile) {
        const thumbExt = thumbnailFile.name.split('.').pop();
        const thumbPath = `thumbs/${crypto.randomUUID()}.${thumbExt}`;

        thumbnailUrl = await uploadFile(thumbnailFile, thumbPath, {
          progressStart: videoFile ? 90 : 0,
          progressEnd: 100,
          label: "Uploading thumbnail",
        });
      }

      setUploadStage(editingId ? "Saving changes" : "Publishing video");

      if (editingId) {
        // Update existing video
        const updateData: Partial<VideoFormState & Pick<PortfolioVideo, "video_url" | "thumbnail_url">> = { ...formData };
        if (videoUrl) updateData.video_url = videoUrl;
        if (thumbnailUrl) updateData.thumbnail_url = thumbnailUrl;

        const { error } = await supabase
          .from("portfolio_videos")
          .update(updateData)
          .eq("id", editingId);

        if (error) throw error;
        toast.success("Video updated successfully");
      } else {
        // Insert new video
        const { error } = await supabase
          .from("portfolio_videos")
          .insert({
            ...formData,
            video_url: videoUrl,
            thumbnail_url: thumbnailUrl || null,
          });

        if (error) throw error;
        toast.success("Video uploaded successfully");
      }

      resetForm();
      await loadVideos();
    } catch (error) {
      const uploadedPathsToRemove = [videoUrl, thumbnailUrl]
        .filter(Boolean)
        .map((url) => {
          const marker = `/storage/v1/object/public/${STORAGE_BUCKET}/`;
          const [, path = ""] = url.split(marker);
          return path;
        })
        .filter(Boolean);

      if (uploadedPathsToRemove.length > 0) {
        const { error: cleanupError } = await supabase.storage
          .from(STORAGE_BUCKET)
          .remove(uploadedPathsToRemove);

        if (cleanupError) {
          console.error("Error cleaning up failed upload assets:", cleanupError);
        }
      }

      console.error("Error saving admin video:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStage(null);
    }
  };

  const handleEdit = (video: PortfolioVideo) => {
    setEditingId(video.id);
    setFormData({
      title: video.title,
      description: video.description || "",
      industry: video.industry,
      location: video.location,
      campaign_type: video.campaign_type || "",
      format_type: video.format_type === "long" || video.format_type === "viral" ? video.format_type : "short",
      views: video.views,
      likes: video.likes,
      comments: video.comments,
      conversions: video.conversions,
      revenue: video.revenue,
      roi_multiplier: video.roi_multiplier,
      testimonial: video.testimonial || "",
      order_index: video.order_index,
      conversion_type: video.conversion_type || "calls",
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this video?")) return;

    try {
      const { error } = await supabase
        .from("portfolio_videos")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success("Video deleted");
      await loadVideos();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to delete video");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      industry: "",
      location: "",
      campaign_type: "",
      format_type: "short",
      views: 0,
      likes: 0,
      comments: 0,
      conversions: 0,
      revenue: 0,
      roi_multiplier: 0,
      testimonial: "",
      order_index: videos.length,
      conversion_type: "calls",
    });
    setVideoFile(null);
    setThumbnailFile(null);
    setUploadProgress(0);
    setUploadStage(null);
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen gradient-mesh py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-bold">Portfolio Videos ({videos.length})</h1>
              <p className="text-sm text-muted-foreground mt-1">Newest uploads now surface first when display order is tied.</p>
            </div>
            <Button
              onClick={() => setShowForm(true)}
              disabled={uploading}
              className="bg-gradient-to-r from-neon-purple to-neon-pink"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Video
            </Button>
          </div>

          {/* Upload Form */}
          {showForm && (
            <div className="glass-morphism border-2 border-neon-purple/30 rounded-xl p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">
                  {editingId ? "Edit Video" : "Upload New Video"}
                </h2>
                <Button variant="ghost" size="sm" onClick={resetForm}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Business Name *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g., Café Berlin"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="campaign_type">Campaign Type *</Label>
                    <Input
                      id="campaign_type"
                      value={formData.campaign_type}
                      onChange={(e) => setFormData({ ...formData, campaign_type: e.target.value })}
                      placeholder="e.g., Happy Hour Campaign"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="format_type">Video Format *</Label>
                    <Select
                      value={formData.format_type}
                      onValueChange={(val) => setFormData({ ...formData, format_type: val as "short" | "long" | "viral" })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select format" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short">⚡ Short (13-15s)</SelectItem>
                        <SelectItem value="long">🎬 Long (30s - 1min)</SelectItem>
                        <SelectItem value="viral">🔥 Viral Edit / Speed Ramp</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry *</Label>
                    <Input
                      id="industry"
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      placeholder="e.g., ☕ Café"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="location">Location *</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="e.g., Berlin"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="views">Views</Label>
                    <Input
                      id="views"
                      type="number"
                      value={formData.views}
                      onChange={(e) => setFormData({ ...formData, views: parseInt(e.target.value) || 0 })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="likes">Likes</Label>
                    <Input
                      id="likes"
                      type="number"
                      value={formData.likes}
                      onChange={(e) => setFormData({ ...formData, likes: parseInt(e.target.value) || 0 })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comments">Comments</Label>
                    <Input
                      id="comments"
                      type="number"
                      value={formData.comments}
                      onChange={(e) => setFormData({ ...formData, comments: parseInt(e.target.value) || 0 })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="conversions">💰 Conversions</Label>
                    <Input
                      id="conversions"
                      type="number"
                      value={formData.conversions}
                      onChange={(e) => setFormData({ ...formData, conversions: parseInt(e.target.value) || 0 })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="conversion_type">Conversion Type *</Label>
                    <Select
                      value={formData.conversion_type}
                      onValueChange={(value) => setFormData({ ...formData, conversion_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select conversion type" />
                      </SelectTrigger>
                <SelectContent>
                  <SelectItem value="calls">Calls</SelectItem>
                  <SelectItem value="bookings">Bookings</SelectItem>
                  <SelectItem value="reservations">Reservations</SelectItem>
                  <SelectItem value="sign-ups">Sign-ups</SelectItem>
                  <SelectItem value="members">Members</SelectItem>
                  <SelectItem value="leads">Leads</SelectItem>
                  <SelectItem value="appointments">Appointments</SelectItem>
                  <SelectItem value="consultations">Consultations</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="store-visits">Store Visits</SelectItem>
                  <SelectItem value="deal-redemptions">Deal Redemptions</SelectItem>
                  <SelectItem value="orders">Orders</SelectItem>
                  <SelectItem value="downloads">Downloads</SelectItem>
                  <SelectItem value="table-scans">Table Scans</SelectItem>
                  <SelectItem value="coupons">Coupons Redeemed</SelectItem>
                </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="revenue">Revenue (€)</Label>
                    <Input
                      id="revenue"
                      type="number"
                      value={formData.revenue}
                      onChange={(e) => setFormData({ ...formData, revenue: parseInt(e.target.value) || 0 })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="roi_multiplier">ROI Multiplier</Label>
                    <Input
                      id="roi_multiplier"
                      type="number"
                      step="0.1"
                      value={formData.roi_multiplier}
                      onChange={(e) => setFormData({ ...formData, roi_multiplier: parseFloat(e.target.value) || 0 })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="order_index">Display Order</Label>
                    <Input
                      id="order_index"
                      type="number"
                      value={formData.order_index}
                      onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="testimonial">Testimonial</Label>
                  <Textarea
                    id="testimonial"
                    value={formData.testimonial}
                    onChange={(e) => setFormData({ ...formData, testimonial: e.target.value })}
                    placeholder="Customer quote..."
                    rows={3}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Video File * {editingId && "(Leave empty to keep current)"}</Label>
                    <Input
                      type="file"
                      accept="video/*"
                      onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                      required={!editingId}
                    />
                    {videoFile && (
                      <div className="space-y-1 text-xs text-muted-foreground">
                        <p>
                          Selected: <span className="text-foreground font-medium">{videoFile.name}</span> · {formatFileSize(videoFile.size)}
                        </p>
                        {videoFile.size > RESUMABLE_UPLOAD_THRESHOLD && (
                          <p>Large file detected — resumable upload will be used automatically.</p>
                        )}
                        {videoFile.size > MAX_VIDEO_SIZE && (
                          <p className="text-destructive font-medium">This file is above the {formatFileSize(MAX_VIDEO_SIZE)} limit.</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Thumbnail Image {editingId && "(Leave empty to keep current)"}</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
                    />
                    {thumbnailFile && (
                      <p className="text-xs text-muted-foreground">
                        Selected: <span className="text-foreground font-medium">{thumbnailFile.name}</span> · {formatFileSize(thumbnailFile.size)}
                      </p>
                    )}
                  </div>
                </div>

                {uploading && (
                  <div className="space-y-2 rounded-lg border border-border bg-background/40 p-4">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="font-medium text-foreground">{uploadStage ?? "Uploading"}</span>
                      <span className="text-muted-foreground">{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-2" />
                    <p className="text-xs text-muted-foreground">Please keep this page open until the upload finishes.</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={uploading}
                  className="w-full bg-gradient-to-r from-neon-purple to-neon-pink"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {uploadStage ?? "Uploading..."}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {editingId ? "Update Video" : "Upload Video"}
                    </>
                  )}
                </Button>
              </form>
            </div>
          )}

          {/* Videos List */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <div
                key={video.id}
                className="glass-morphism border-2 border-border rounded-xl overflow-hidden hover-lift"
              >
                <div className="aspect-[9/16] bg-muted relative">
                  {video.thumbnail_url ? (
                    <img
                      src={video.thumbnail_url}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <video
                      src={video.video_url}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="p-4 space-y-3">
                  <div>
                    <h3 className="font-bold text-lg">{video.title}</h3>
                    <p className="text-sm text-muted-foreground">{video.campaign_type}</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{video.industry}</span>
                    <span>•</span>
                    <span>{video.location}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>👁️ {video.views.toLocaleString()}</div>
                    <div>❤️ {video.likes.toLocaleString()}</div>
                    <div>💬 {video.comments.toLocaleString()}</div>
                    <div className="text-green-500">📞 {video.conversions}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(video)}
                      className="flex-1"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(video.id)}
                      className="flex-1 text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {videos.length === 0 && !showForm && (
            <div className="text-center py-12 glass-morphism rounded-xl">
              <p className="text-muted-foreground mb-4">No videos yet</p>
              <Button
                onClick={() => setShowForm(true)}
                className="bg-gradient-to-r from-neon-purple to-neon-pink"
              >
                <Plus className="w-4 h-4 mr-2" />
                Upload First Video
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminVideos;
